// src/components/ui/SectionCard.tsx
// A numbered/lettered section wrapper for a long form — the multi-column,
// glanceable-status alternative to a plain <div className="card"> that
// PreConsultStage's linear stack used before. See
// docs/HMS_V4_UI_MODERNIZATION_ASSESSMENT.md backlog item 7.
//
// `index` follows display order, not field identity — reordering a
// template's sections in the Admin Panel relabels A/B/C along with it. That
// is a deliberate, stated trade-off (see the assessment's decision log,
// item 4): letters are for "which card are we talking about right now" in a
// conversation between staff, not a stable identifier anything depends on.
import type { ReactNode } from 'react';
import { Badge } from './Badge';
import { cn } from '@/lib/cn';

export type SectionStatus = 'complete' | 'partial' | 'incomplete';

const STATUS_LABEL: Record<SectionStatus, string> = {
  complete: 'Complete',
  partial: 'In progress',
  incomplete: 'Not started',
};
const STATUS_VARIANT: Record<SectionStatus, 'success' | 'warn' | 'neutral'> = {
  complete: 'success',
  partial: 'warn',
  incomplete: 'neutral',
};

export interface SectionCardProps {
  /** A letter or number shown in a small badge before the title, e.g. "A". Omit for a section that isn't part of the lettered sequence (e.g. a utility block like document upload). */
  index?: string;
  title: string;
  status?: SectionStatus;
  /** Extra content in the header row, e.g. a "view full" link. */
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  /** Widens the card to two tracks of StageLayout's `three-up` grid — the
   * Pre-Consult redesign's D/F cards (a diet matrix, a due-checklist table)
   * need more than one column's width. A hint, not a guarantee: below `xl`
   * every card is full width regardless (see
   * docs/HMS_V4_ASSESSMENT_FIELD_TYPES_SPEC.md §4). Default 1, i.e. no span. */
  span?: 1 | 2;
}

export function SectionCard({ index, title, status, action, children, className, span }: SectionCardProps) {
  return (
    // The header sits on its own tinted band, separated by a rule. On a screen
    // of a dozen stacked cards that band is what makes each one read as a
    // discrete block rather than the page being one continuous field of white
    // — the structure the wireframe conveys with its own fills. Deliberately a
    // NEUTRAL token: semantic colour on this screen is reserved for clinical
    // state (the status badge, a flagged vital), and tinting every header by
    // status would drown those out.
    // flex + h-full: the grid row stretches every card to the tallest
    // sibling's height (StageLayout's `three-up` uses items-stretch, the grid
    // default), and this makes the card itself fill that stretched height
    // rather than staying its own content height with dead space beneath it.
    <div className={cn('flex h-full flex-col overflow-hidden rounded-[var(--radius)] border border-line bg-card', span === 2 && 'xl:col-span-2', className)}>
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-b border-line bg-muted px-3.5 py-2">
        <div className="flex items-center gap-2 min-w-0">
          {index && (
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-card text-[10.5px] font-bold text-ink-muted">
              {index}
            </span>
          )}
          <span className="truncate text-[11.5px] font-bold uppercase tracking-wide text-ink-muted">{title}</span>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {status && <Badge variant={STATUS_VARIANT[status]}>{STATUS_LABEL[status]}</Badge>}
          {action}
        </div>
      </div>
      <div className="flex-1 p-3.5">{children}</div>
    </div>
  );
}
